/**
 * Photo and scanner tips, by device (from the user agent; anything unrecognised gets the general line).
 * The phone's own document scanner first (it crops and flattens the page and gives a PDF), then four
 * short photo rules in the order Matt set (2026-10-19): flat on a table, light, straight above, hold still.
 * Desktops get one line instead: download the PDF from the payroll portal.
 */
export type ScannerDevice = 'iphone' | 'samsung' | 'android' | 'phone' | 'desktop';

export function scannerDevice(ua: string, touchFirst: boolean): ScannerDevice {
  // iPadOS asks for the desktop site: a Mac user agent on a touch screen.
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && touchFirst)) return 'iphone';
  // Samsung Internet, or a Samsung model number ("SM-S931B"); Chrome's reduced user agent hides the model.
  if (/SamsungBrowser/i.test(ua) || /\bSM-[A-Z0-9]/.test(ua)) return 'samsung';
  if (/Android/i.test(ua)) return 'android';
  return touchFirst ? 'phone' : 'desktop';
}

/**
 * The tips in order, as i18n keys with their icon; desktop: just the payroll-portal line. `form106`: the texts that
 * name the document say "Form 106", not "payslip" (`<key>.form106`).
 */
export function photoTipKeys(device: ScannerDevice, form106 = false): { key: string }[] {
  const doc = (key: string) => (form106 ? `${key}.form106` : key);
  if (device === 'desktop') return [{ key: doc('tips.desktop') }];
  // The first is the scanner line, the rest are the numbered photo steps.
  return [
    { key: `tips.scanner.${device}` },
    { key: doc('tips.flat') },
    { key: 'tips.light' },
    { key: 'tips.straight' },
    { key: 'tips.still' },
  ];
}
